import {
  AccountSearchResult,
  CaseServiceDetailsResponse,
  ClmUserInfo,
  GenerateEmailResponse,
  LanguageEnum,
  PartnerAddress,
  type ProductCatalogueResponse,
  ProductServiceOutput,
} from 'pco-product-service-internal-consumer';

export type AccountType = {
  accountSearchState?: string;
  isLoading: boolean;
  brUid: string | undefined;
  accountTable: AccountTableState;
  accountNumberFromParam?: string;
  newAccountsTable: {
    statusFilter: string[];
    typeFilter: string[];
  };
  error?: CustomError;
  accountDetails?: AccountSearchResult;
  contactNotes?: string;
};

export type AccountTableState = {
  statusFilter: string[];
  typeFilter: string[];
  selectedProduct?: string;
  tableItems: ProductServiceOutput[];
};

export enum AccountShownPageType {
  PRODUCT = 'product',
  CASE = 'case',
  NEW_MANDATE = 'NEW_MANDATE',
  CHANGE_MANDATE = 'CHANGE_MANDATE',
  PRODUCT_CONFIGURATION = 'PRODUCT_CONFIGURATION',
  PRODUCT_DETAILS = 'PRODUCT_DETAILS',
  SERVICE_ROLE = 'SERVICE_ROLE',
  ORDER_NEW_PRODUCT_OR_SERVICE = 'ORDER_NEW_PRODUCT_OR_SERVICE',
  PRODUCT_MODIFICATION = 'PRODUCT_MODIFICATION',
}

export type AppConfig = {
  clmUrl: string;
  msdUrl: string;
  cmpUrl: string;
  brManagementUrl: string;
  userId: string;
  env: string;
};
export type CaseStateType = {
  isLoading: boolean;
  error?: CustomError;
  selectedToDistribute: string[];
  case?: CaseServiceDetailsResponse;
};

export enum ProposalDocumentClassTypes {
  FACT_SHEET = 'FACT_SHEET',
  FACT_SHEET_ONEPAGER = 'FACT_SHEET_ONEPAGER',
  PRICE_LIST = 'PRICE_LIST',
  CLIENT_PRESENTATION = 'CLIENT_PRESENTATION',
  CLIENT_PRESENTATION_SHORT = 'CLIENT_PRESENTATION_SHORT',
  CONTRACT_FULL = 'CONTRACT_FULL',
  CIPPI2 = 'CIPPI2',
}

export enum DocumentStatuses {
  // MSD statuses
  READY_TO_ORDER = 'READY_TO_ORDER', //' - '
  ORDERED = 'ORDERED', // PENDING -- client prese
  COMPLETED = 'COMPLETED', // READY on MOCKUP --> but i will se it for client pres
  FAILED = 'FAILED',
  // PCD statuses
  PENDING = 'PENDING',
  SENT = 'SENT',
  ACTIVE = 'ACTIVE',
  OFFERED = 'OFFERED',
}

export type CustomError = {
  code?: string;
  message?: string;
};
export type MandateRequest = {
  isRequiredNewBr?: boolean;
  br?: string;
  partnerNumberId?: string;
  placeHolderName?: string;
  caseId?: number;
};

export type MandateType = {
  error?: CustomError;
  isLoading: boolean;
  mandateRequest?: MandateRequest;
};
export type MemberCodeStatus = {
  isLoading: boolean;
  isGenerateCodeLoading: boolean;
  generateCodeError?: CustomError;
  fetchCodeError?: CustomError;
  distributionRequest?: GenerateEmailResponse;
};

export enum PartnerType {
  INDIVIDUAL_PARTNER = 'PI',
  LEGAL_PARTNER = 'LE',
}
export enum StepMetadataType {
  CONFIGURATION_FORM_METADATA = 'CONFIGURATION_FORM_METADATA',
  CONFIGURATION_SERVICE_ROLE_METADATA = 'CONFIGURATION_SERVICE_ROLE_METADATA',
  CUSTOM_COMPONENT = 'CUSTOM_COMPONENT',
  INFORMATION_METADATA = 'INFORMATION_METADATA',
  PREAPPROVAL_FORM_METADATA = 'PREAPPROVAL_FORM_METADATA',
}
export type TableState = {
  statusFilter: string[];
  typeFilter: string[];
  selectedProduct?: string;
  tableItems: NewProductOrServiceTableRow[];
  isLoading: boolean;
};
export type VideoIdentificationFormType = {
  partnerUid?: string;
  businessRelationshipNumbers: string;
  eMailAddresses: string;
  mobileNumbers: string;
  name: {
    firstName: string;
    middleName: string;
    lastName: string;
    gender: string;
  };
  address: PartnerAddress;
  dateOfBirth: string;
  language?: LanguageEnum;
  nationalities: string;
  legalInformation: boolean;
  generalBankingConditions: boolean;
  clmUserInfo: ClmUserInfo;
  advisoryLocationConfirmation: boolean;
};

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER',
}

export enum LanguagesEnum {
  EN = 'English',
  DE = 'German',
  FR = 'French',
  IT = 'Italian',
}

export enum VideoLanguages {
  EN = 'EN',
  DE = 'DE',
  FR = 'FR',
  IT = 'IT',
}

export type NewProductOrServiceTableRow = {
  id: string;
  serviceDefinitionName?: string;
  portfolio?: string;
  category?: string;
  agreementStatus: string;
  view: ProductOrServiceType;
  family: string;
  modifiedAt: string;
};

export enum ProductOrServiceType {
  MEMBERZONE = 'MEMBERZONE',
  CASE = 'CASE',
  PRODUCT = 'PRODUCT',
  VIDEO_IDENTIFICATION = 'VIDEO_IDENTIFICATION',
}

export enum NewAccountEnum {
  NEW = 'NEW',
}
// These are the workflow statuses

export enum InOpening {
  INITIATED = 'INITIATED',
  READY_FOR_CONFIGURATION = 'READY_FOR_CONFIGURATION',
  CONFIGURED = 'CONFIGURED',
  READY_FOR_PRE_APPROVAL = 'READY_FOR_PRE_APPROVAL',
  PRE_APPROVED = 'PRE_APPROVED',
  REWORK_NEEDED = 'REWORK_NEEDED',
  READY_FOR_DISTRIBUTION = 'READY_FOR_DISTRIBUTION',
  PENDING = 'PENDING',
  IN_VALIDATION = 'IN_VALIDATION',
  VALIDATION_COMPLETED = 'VALIDATION_COMPLETED',
  READY_FOR_SETUP = 'READY_FOR_SETUP',
  READY_FOR_APPROVAL = 'READY_FOR_APPROVAL',
  APPROVED = 'APPROVED',
  IN_ACTIVATION = 'IN_ACTIVATION',
  CLARIFICATION_NEEDED = 'CLARIFICATION_NEEDED',
  IN_CONFIGURATION = 'IN_CONFIGURATION',
  IN_PRE_APPROVAL = 'IN_PRE_APPROVAL',
  IN_SETUP = 'IN_SETUP',
}

export enum Active {
  ACTIVE = 'ACTIVE',
  CLOSING = 'IN_CLOSING',
  IN_MODIFICATION = 'IN_MODIFICATION',
}

export enum Inactive {
  WITHDRAWN = 'WITHDRAWN',
  INACTIVE = 'INACTIVE',
  DELETED = 'DELETED',
  REJECTED = 'REJECTED',
  PRE_APPROVAL_REJECTED = 'PRE_APPROVAL_REJECTED',
}

export function determineStatusFromEnum(status: string | undefined): string {
  if (!status) return 'Unknown';
  if (Object.values(InOpening).includes(status as InOpening)) {
    return 'In Opening';
  } else if (Object.values(Active).includes(status as Active)) {
    return 'Active';
  } else if (Object.values(Inactive).includes(status as Inactive)) {
    return 'Inactive';
  } else {
    console.log('Unknown status: ' + status);
    return status;
  }
}

export const AlternateInvestmentStatus = {
  READY_FOR_CONFIGURATION: InOpening.READY_FOR_CONFIGURATION,
  READY_FOR_DISTRIBUTION: InOpening.READY_FOR_DISTRIBUTION,
  IN_ACTIVATION: InOpening.IN_ACTIVATION,
  ACTIVE: Active.ACTIVE,
  CLOSING: Active.CLOSING,
  INACTIVE: Inactive.INACTIVE,
  WITHDRAWN: Inactive.WITHDRAWN,
} as const;

export type AlternateInvestmentStatus = (typeof AlternateInvestmentStatus)[keyof typeof AlternateInvestmentStatus];
// This is the type for eligible products
export type ProductStateType = {
  products: ProductCatalogueResponse[];
  error: boolean;
  isLoading: boolean;
};
